import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuth";
import { formatApiError } from "@/lib/api";
import { Snowflake, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const inputCls =
  "w-full bg-[#050810] border border-[#1A2642] text-white rounded-md px-4 py-3 text-sm placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition";
const labelCls = "block text-[11px] font-bold uppercase tracking-[0.18em] text-[#94A3B8] mb-2";

export default function ResetPassword() {
  const { resetPassword } = useCustomerAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (pwd !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await resetPassword(token, pwd);
      setDone(true);
      toast.success("Mot de passe réinitialisé ✓");
      setTimeout(() => nav("/"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md glass border border-[#1A2642] rounded-2xl p-6 sm:p-8" data-testid="reset-password-page">
        <div className="flex justify-center mb-6"><Logo className="h-10 w-auto" /></div>

        {!token ? (
          <div className="text-center">
            <p className="text-[#EF476F] font-semibold mb-4" data-testid="reset-no-token">Lien invalide ou incomplet.</p>
            <Link to="/" className="text-[#00B4D8] text-sm font-semibold hover:underline">Retour à l'accueil</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-[#06D6A0] mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold mb-2">Mot de passe modifié</h1>
            <p className="text-sm text-[#94A3B8]">Redirection vers l'accueil…</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Snowflake className="w-6 h-6 text-[#00B4D8]" />
              <span className="font-heading font-extrabold text-lg">Nouveau mot de passe</span>
            </div>
            <p className="text-sm text-[#94A3B8] mb-5">Choisissez un nouveau mot de passe pour votre compte ClimOmax13.</p>

            {error && <div className="bg-[#EF476F]/15 border border-[#EF476F]/30 text-[#EF476F] text-sm rounded-md px-3 py-2 mb-4" data-testid="reset-error">{error}</div>}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className={labelCls}>Nouveau mot de passe *</label>
                <input required type="password" minLength={6} className={inputCls} data-testid="reset-password" placeholder="••••••••" value={pwd} onChange={(e) => setPwd(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Confirmer le mot de passe *</label>
                <input required type="password" minLength={6} className={inputCls} data-testid="reset-confirm" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full bg-[#FFD60A] text-[#050810] font-bold rounded-full py-3.5 hover:bg-[#FFEA00] disabled:opacity-50 transition flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Réinitialiser
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
